import LMPAccount from "../api/lmp/LMPAccount.ts";
import LMPErrorResponse from "../api/lmp/LMPErrorResponse.ts";
import LMPAuthApi from "../api/lmp/LMPAuthApi";
import LMPTerm from "../api/lmp/LMPTerm.ts";
import LocalStorageKey from "../common/enums/LocalStorageKey";

export default class AuthService {
    api;

    constructor() {
        this.api = new LMPAuthApi();
    }

    async join({ userid, password, birthday, isSubscribe }) {
        try {
            birthday = birthday.replaceAll(".", "");
            const response = await this.api.join({ userid, password, birthday, isSubscribe });
            return new LMPAccount(response.account);
        } catch (err) {
            const error = new LMPErrorResponse(err.response);

            if (error.status === 406) {
                const code = error.data.error.code;
                if (code === "MR.002.01") {
                    throw new Error("아이디 중복");
                } else if (code === "MR.002.02") {
                    throw new Error("암호 규격 맞지 않음");
                } else if (code === "MR.002.04") {
                    throw new Error("나이 제한");
                } else if (code === "MR.002.07") {
                    throw new Error("로그인이 필요한 경우");
                } else if (code === "MR.002.08") {
                    throw new Error("이메일 인증 필요");
                } else if (code === "MR.002.11") {
                    throw new Error("탈퇴한 회원");
                }
            }

            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async resendJoinMail({ userId }) {
        try {
            await this.api.resendJoinMail({ userId });
        } catch (e) {
            const error = new LMPErrorResponse(e.response);

            // 그 외의 에러는 원래 메시지를 던집니다.
            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async login({ userid, password }) {
        try {
            const response = await this.api.login({ userid, password });
            const userInfo = await this.api.getUserInfo({ loginSessionID: response.account.loginSessionID });

            const account = new LMPAccount(userInfo.account);
            // LGM 세션 아이디 저장
            // localStorage.setItem(LocalStorageKey.lgmSessionId, response.account.loginSessionID);
            // localStorage.setItem(LocalStorageKey.userNo, account.userNo);
            // localStorage.setItem(LocalStorageKey.role, Role.CallCenter);
            // localStorage.setItem(LocalStorageKey.displayUserID, account.displayUserID);

            // 성공 시 account 정보를 반환할 수 있습니다.
            return response;
        } catch (err) {
            const error = new LMPErrorResponse(err.response);

            if (error.status === 406) {
                const code = error.data.error.code;
                if (code === "MS.001.02" || code === "MS.001.01") {
                    throw new Error("ID or password is not correct.");
                } else if (code === "MS.001.04") {
                    await this.resendJoinMail({ userId: userid });
                    throw new Error(`/auth/message?email=${userid}`);
                } else if (code === "MS.001.03") {
                    throw new Error("타 국가 계정입니다.");
                } else if (code === "MS.001.08") {
                    throw new Error(`/auth/login-lock?userId=${userid}`);
                } else if (code === "MS.001.09") {
                    throw new Error("Debug: 탈퇴한 회원입니다.");
                }
            }
            // 그 외의 에러는 원래 메시지를 던집니다.
            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async unlockLogin({ userId, authKeyNum }) {
        try {
            await this.api.unlockLogin({ userId, authKeyNum });
        } catch (e) {
            const error = new LMPErrorResponse(e.response);

            if (error.status === 406) {
                const code = error.data.error.code;
                if (code === "MS.004.01") {
                    throw new Error("Invalid OTP Key");
                } else if (code === "MS.004.02") {
                    throw new Error("Not Matched User Info");
                } else if (code === "MS.004.03") {
                    throw new Error("Sign-In Already Unlocked");
                }
            }

            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async resendUnlockLoginMail({ userId }) {
        try {
            await this.api.resendUnlockLoginMail({ userId });
        } catch (e) {
            const error = new LMPErrorResponse(e.response);

            if (error.status === 406) {
                const code = error.data.error.code;
                if (code === "MI.012.01") {
                    throw new Error("Sign-In Already Unlocked");
                }
            }

            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async verifyJoinEmail({ authKeyNum }) {
        try {
            await this.api.verifyJoinEmail({ authKeyNum });
        } catch (e) {
            const error = new LMPErrorResponse(e.response);

            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async sendResetPasswordMail({ userid }) {
        try {
            await this.api.sendResetPasswordMail({ userid });
        } catch (e) {
            const error = new LMPErrorResponse(e.response);

            if (error.status === 406) {
                const code = error.data.error.code;
                if (code === "MI.008.01") {
                    throw new Error("존재하지 않는 아이디입니다.");
                } else if (code === "MI.008.02") {
                    throw new Error("계정 인증이 되지 않았습니다.");
                } else if (code === "MI.008.04") {
                    throw new Error("동일 계정으로 5분 이내에 5번 재시도시 5분간 재시도 불가");
                }
            }

            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async resetPassword({ password, authKeyNum }) {
        try {
            await this.api.resetPassword({ password: password, authKeyNum: authKeyNum });
        } catch (e) {
            const error = new LMPErrorResponse(e.response);

            if (error.status === 406) {
                const code = error.data.error.code;
                if (code === "MI.004.03") {
                    throw new Error("유효하지 않은 OTP 코드");
                } else if (code === "MI.004.04") {
                    throw new Error("이전 Password 와 같은 값으로 변경하려고 하는 경우");
                } else if (code === "MI.004.05") {
                    throw new Error("최근 3회 Password 와 같은 값으로 변경하려고 하는 경우");
                }
            }

            throw new Error(error.data.error.message || "An unknown error occurred.");
        }
    }

    async getTerm({ termData }) {
        try {
            const response = await this.api.getTerm({ termData });
            return new LMPTerm(response.info.terms[0]);
        } catch (e) {
            const error = new LMPErrorResponse(e.response);

            if (error.status === 406) {
                const code = error.data.error.code;
                if (code === "IF.001.01") {
                    throw new Error("해당 사업자에 대한 약관 유형코드가 맞지 않는 경우");
                }
            }
        }
    }

    async logout() {
        // LMP 세션 삭제 시도 — 실패해도(세션 만료/없음 포함) 로컬 세션은 반드시 정리
        try {
            const loginSessionID = localStorage.getItem(LocalStorageKey.lgmSessionId);
            if (loginSessionID) {
                await this.api.removeSession({ loginSessionID });
            }
        } catch (e) {
            // SE.003.01: LMP 쪽에 이미 세션이 없는 경우 — 로그아웃으로 간주하고 계속 진행
            console.warn("LMP removeSession failed (계속 진행):", e?.response?.data?.error?.code || e?.message);
        }
        localStorage.removeItem(LocalStorageKey.lgmSessionId);
        localStorage.removeItem(LocalStorageKey.userNo);
        localStorage.removeItem(LocalStorageKey.role);
        localStorage.removeItem(LocalStorageKey.displayUserID);
        localStorage.removeItem(LocalStorageKey.accessToken);
        localStorage.removeItem(LocalStorageKey.refreshToken);
    }
}
